import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import PropTypes from 'prop-types';
import { Typography, Button, Grid, Dialog, DialogTitle, DialogContent, DialogActions, TextField, IconButton, Tooltip, Switch } from '@mui/material';
import { Fullscreen as FullscreenIcon, FullscreenExit as FullscreenExitIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useAddProductContentMutation, usePutProductContentMutation } from '../features/api/rest/apiProductContentsSlice';

function ProductOrganizationBoardDialog({ ean, actionType, isOpen, setIsOpen, id = 0, oldContent = '', oldPublic = false }) {
  let title = '';
  let handleConfirm = () => {};
  const { t } = useTranslation();
  const { opk } = useParams();
  const [dialogFullscreen, setDialogFullscreen] = useState(false);
  const [dialogContent, setDialogContent] = useState(oldContent || '');
  const [dialogIsPublic, setDialogIsPublic] = useState(oldPublic || false);
  const [addContent] = useAddProductContentMutation();
  const [putContent] = usePutProductContentMutation();
  useEffect(() => {
    if (isOpen) {
      setDialogFullscreen(false);
      setDialogContent(oldContent || '');
      setDialogIsPublic(oldPublic || false);
    }
  }, [isOpen]);
  const handleAdd = async () => {
    try {
      await addContent({ opk, ean, payload: { content: dialogContent, public: dialogIsPublic } });
    } finally {
      setIsOpen(false);
    }
  };
  const handleEdit = async () => {
    try {
      await putContent({ opk, ean, id, payload: { content: dialogContent, public: dialogIsPublic } });
    } finally {
      setIsOpen(false);
    }
  };
  switch (actionType) {
    case 'add':
      title = t('organizations.product.board.addMessage');
      handleConfirm = handleAdd;
      break;
    case 'edit':
      title = t('organizations.product.board.edit');
      handleConfirm = handleEdit;
      break;
    default:
      title = '';
  }

  return (
    <Dialog open={isOpen} onClose={() => setIsOpen(false)} fullWidth fullScreen={dialogFullscreen}>
      <DialogTitle sx={{ textAlign: 'center' }}>
        <Tooltip title={dialogFullscreen ? t('organizations.product.board.exitFullscreen') : t('organizations.product.board.fullscreen')} placement="right">
          <IconButton onClick={() => setDialogFullscreen(!dialogFullscreen)} size="large" sx={{ position: 'absolute', top: 8, left: 8 }}>
            {dialogFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
          </IconButton>
        </Tooltip>
        {title}
      </DialogTitle>
      <DialogContent>
        <TextField fullWidth sx={{ mt: 2 }} label={t('organizations.product.board.message')} required value={dialogContent} multiline rows={8} variant="outlined" onChange={(e) => setDialogContent(e.target.value)} />
        <Grid container display="flex" alignItems="center" justifyContent="left" p={0} pt={2}>
          <Grid item>
            <Switch checked={dialogIsPublic} onChange={(e) => setDialogIsPublic(e.target.checked)} />
          </Grid>
          <Grid item>
            <Typography>{t(`organizations.product.board.public`)}</Typography>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ pt: 0, pb: 3, px: 3 }}>
        <Grid container display="flex" justifyContent="flex-end" gap={1}>
          <Grid item>
            <Button type="submit" variant="outlined" color="secondary" onClick={() => setIsOpen(false)}>
              {t('organizations.form.close')}
            </Button>
          </Grid>
          <Grid item>
            <Button type="submit" variant="contained" color="primary" onClick={handleConfirm}>
              {t('common.actions.save')}
            </Button>
          </Grid>
        </Grid>
      </DialogActions>
    </Dialog>
  );
}

ProductOrganizationBoardDialog.propTypes = {
  ean: PropTypes.string.isRequired,
  actionType: PropTypes.string.isRequired,
  isOpen: PropTypes.bool.isRequired,
  setIsOpen: PropTypes.func.isRequired,
  id: PropTypes.number,
  oldContent: PropTypes.string,
  oldPublic: PropTypes.bool,
};

export default ProductOrganizationBoardDialog;
